import { PRESETS, type Chemistry, type Material, type Preset, type Vec3 } from '../gl/presets'

export interface TuningPanelProps {
  preset: Preset
  presetId: string
  onPresetChange: (id: string) => void
  onMaterialChange: (patch: Partial<Material>) => void
  onChemistryChange: (patch: Partial<Chemistry>) => void
  onReset: () => void
  onClear: () => void
}

/**
 * The rim colours are linear HDR triples, which are miserable to art-direct
 * as three numbers. These read hue / saturation / level off a triple and put
 * one back, so each slider moves the one property it names and leaves the
 * other two alone. Value is allowed above 1 — these are lights, not pixels.
 */
function value(c: Vec3): number {
  return Math.max(c[0], c[1], c[2])
}

function saturation(c: Vec3): number {
  const v = value(c)
  return v === 0 ? 0 : (v - Math.min(c[0], c[1], c[2])) / v
}

function hue(c: Vec3): number {
  const v = value(c)
  const min = Math.min(c[0], c[1], c[2])
  const d = v - min
  if (d === 0) return 0
  let h: number
  if (v === c[0]) h = ((c[1] - c[2]) / d) % 6
  else if (v === c[1]) h = (c[2] - c[0]) / d + 2
  else h = (c[0] - c[1]) / d + 4
  return ((h / 6) + 1) % 1
}

function fromHsv(h: number, s: number, v: number): Vec3 {
  const i = Math.floor(h * 6)
  const f = h * 6 - i
  const p = v * (1 - s)
  const q = v * (1 - f * s)
  const t = v * (1 - (1 - f) * s)
  switch (i % 6) {
    case 0: return [v, t, p]
    case 1: return [q, v, p]
    case 2: return [p, v, t]
    case 3: return [p, q, v]
    case 4: return [t, p, v]
    default: return [v, p, q]
  }
}

function withHue(c: Vec3, h: number): Vec3 {
  return fromHsv(h, saturation(c), value(c))
}

function withSaturation(c: Vec3, s: number): Vec3 {
  return fromHsv(hue(c), s, value(c))
}

function withValue(c: Vec3, v: number): Vec3 {
  return fromHsv(hue(c), saturation(c), v)
}

interface SliderProps {
  label: string
  value: number
  min: number
  max: number
  step: number
  onChange: (value: number) => void
}

function Slider({ label, value, min, max, step, onChange }: SliderProps) {
  return (
    <label className="tuning__row">
      <span className="tuning__label">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <span className="tuning__value">{value.toFixed(step < 0.01 ? 4 : 2)}</span>
    </label>
  )
}

/**
 * Live controls for the look. The material constants are far easier to judge
 * by eye than to reason about, so they are adjustable rather than hard-coded.
 *
 * Deliberately has no on-screen affordance — it is a workbench for tuning the
 * artwork, not part of the site. The caller mounts it on a key chord and
 * unmounts it again; see App.
 */
export function TuningPanel({
  preset,
  presetId,
  onPresetChange,
  onMaterialChange,
  onChemistryChange,
  onReset,
  onClear,
}: TuningPanelProps) {
  const { material, chemistry } = preset

  return (
    <div className="tuning tuning--open">
        <div className="tuning__body">
          <p className="tuning__hint">shift + T or esc to hide</p>
          <label className="tuning__row tuning__row--select">
            <span className="tuning__label">pattern</span>
            <select value={presetId} onChange={(event) => onPresetChange(event.target.value)}>
              {PRESETS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>

          <p className="tuning__group">chemistry</p>
          <Slider
            label="feed" value={chemistry.f} min={0.01} max={0.1} step={0.0005}
            onChange={(f) => onChemistryChange({ f })}
          />
          <Slider
            label="kill" value={chemistry.k} min={0.04} max={0.07} step={0.0005}
            onChange={(k) => onChemistryChange({ k })}
          />
          <Slider
            label="speed" value={chemistry.steps} min={1} max={30} step={1}
            onChange={(steps) => onChemistryChange({ steps })}
          />
          <Slider
            label="variance" value={chemistry.variance} min={0} max={1} step={0.01}
            onChange={(variance) => onChemistryChange({ variance })}
          />
          <Slider
            label="evolve" value={chemistry.evolve} min={0} max={0.12} step={0.001}
            onChange={(evolve) => onChemistryChange({ evolve })}
          />
          <Slider
            label="fall" value={chemistry.drift} min={0} max={0.06} step={0.001}
            onChange={(drift) => onChemistryChange({ drift })}
          />
          <Slider
            label="rate vary" value={chemistry.flowVary} min={0} max={1} step={0.01}
            onChange={(flowVary) => onChemistryChange({ flowVary })}
          />
          <Slider
            label="sideways" value={chemistry.flowLateral} min={0} max={1.5} step={0.01}
            onChange={(flowLateral) => onChemistryChange({ flowLateral })}
          />
          <Slider
            label="current size" value={chemistry.flowScale} min={0.4} max={6} step={0.1}
            onChange={(flowScale) => onChemistryChange({ flowScale })}
          />
          <Slider
            label="current shift" value={chemistry.flowEvolve} min={0} max={0.3} step={0.005}
            onChange={(flowEvolve) => onChemistryChange({ flowEvolve })}
          />
          <Slider
            label="mark hold" value={chemistry.markPin} min={0.005} max={0.5} step={0.005}
            onChange={(markPin) => onChemistryChange({ markPin })}
          />
          <Slider
            label="mark level" value={chemistry.markLevel} min={0.1} max={0.8} step={0.01}
            onChange={(markLevel) => onChemistryChange({ markLevel })}
          />
          <Slider
            label="top feed" value={chemistry.emit} min={0} max={1} step={0.02}
            onChange={(emit) => onChemistryChange({ emit })}
          />

          <p className="tuning__group">material</p>
          <Slider
            label="charcoal" value={material.matteAlbedo[0]} min={0.01} max={0.2} step={0.002}
            onChange={(v) => onMaterialChange({ matteAlbedo: [v, v * 1.04, v * 1.15] })}
          />
          <Slider
            label="matte rough" value={material.matteRough} min={0.3} max={1} step={0.01}
            onChange={(matteRough) => onMaterialChange({ matteRough })}
          />
          <Slider
            label="gloss rough" value={material.glossRough} min={0.01} max={0.4} step={0.005}
            onChange={(glossRough) => onMaterialChange({ glossRough })}
          />
          <Slider
            label="relief" value={material.relief} min={0} max={16} step={0.25}
            onChange={(relief) => onMaterialChange({ relief })}
          />
          <Slider
            label="edge lo" value={material.edge[0]} min={0} max={0.5} step={0.01}
            onChange={(v) => onMaterialChange({ edge: [v, Math.max(v + 0.02, material.edge[1])] })}
          />
          <Slider
            label="edge hi" value={material.edge[1]} min={0.02} max={0.7} step={0.01}
            onChange={(v) => onMaterialChange({ edge: [Math.min(material.edge[0], v - 0.02), v] })}
          />
          <Slider
            label="grain" value={material.microGrain} min={0} max={0.8} step={0.01}
            onChange={(microGrain) => onMaterialChange({ microGrain })}
          />
          <Slider
            label="swell" value={material.swell} min={0} max={0.6} step={0.01}
            onChange={(swell) => onMaterialChange({ swell })}
          />
          <Slider
            label="swell size" value={material.swellScale} min={0.5} max={8} step={0.1}
            onChange={(swellScale) => onMaterialChange({ swellScale })}
          />
          <Slider
            label="exposure" value={material.exposure} min={0.2} max={3} step={0.02}
            onChange={(exposure) => onMaterialChange({ exposure })}
          />
          <Slider
            label="vignette" value={material.vignette} min={0} max={1} step={0.02}
            onChange={(vignette) => onMaterialChange({ vignette })}
          />

          <p className="tuning__group">rim light</p>
          <Slider
            label="strength" value={material.rimGain} min={0} max={2} step={0.02}
            onChange={(rimGain) => onMaterialChange({ rimGain })}
          />
          <Slider
            label="focus" value={material.rimFocus} min={0.4} max={5} step={0.1}
            onChange={(rimFocus) => onMaterialChange({ rimFocus })}
          />
          <Slider
            label="spread" value={material.rimSpread} min={0} max={1} step={0.02}
            onChange={(rimSpread) => onMaterialChange({ rimSpread })}
          />
          <Slider
            label="warm level" value={value(material.rimWarm)} min={0} max={3} step={0.05}
            onChange={(v) => onMaterialChange({ rimWarm: withValue(material.rimWarm, v) })}
          />
          <Slider
            label="warm hue" value={hue(material.rimWarm)} min={0} max={1} step={0.005}
            onChange={(h) => onMaterialChange({ rimWarm: withHue(material.rimWarm, h) })}
          />
          <Slider
            label="warm sat" value={saturation(material.rimWarm)} min={0} max={1} step={0.02}
            onChange={(t) => onMaterialChange({ rimWarm: withSaturation(material.rimWarm, t) })}
          />
          <Slider
            label="cool level" value={value(material.rimCool)} min={0} max={3} step={0.05}
            onChange={(v) => onMaterialChange({ rimCool: withValue(material.rimCool, v) })}
          />
          <Slider
            label="cool hue" value={hue(material.rimCool)} min={0} max={1} step={0.005}
            onChange={(h) => onMaterialChange({ rimCool: withHue(material.rimCool, h) })}
          />
          <Slider
            label="cool sat" value={saturation(material.rimCool)} min={0} max={1} step={0.02}
            onChange={(t) => onMaterialChange({ rimCool: withSaturation(material.rimCool, t) })}
          />

          <div className="tuning__actions">
            <button type="button" onClick={onReset}>reseed</button>
            <button type="button" onClick={onClear}>defaults</button>
          </div>
        </div>
    </div>
  )
}
